/**
 * Shared template preview dialog.
 *
 * Renders the email body (sandboxed iframe so CTA buttons can't navigate
 * the admin page away) and, when the template's output_modes include
 * pdf_attachment, an additional PDF tab loaded as a data: URI.
 *
 * Used by both the merged Email Templates list (eye button per row) and
 * the editor header (Preview button next to Test send / Disable / Save)
 * so admins get the same render in both places.
 */

import { useEffect, useState } from 'react'
import { Paperclip } from 'lucide-react'

import api from '@/admin/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

import type { PreviewResponse } from './types'

export interface PreviewTemplateShape {
  id: number
  name: string
  slug: string
  kind: string
}

export function PreviewDialog({
  template,
  onClose,
  draft,
}: {
  template: PreviewTemplateShape
  onClose: () => void
  /**
   * The editor's current content, so preview renders what the admin is looking
   * at rather than the saved row. Omitted by the list page, which has no editor.
   */
  draft?: { subject: string; body_html: string; body_text: string }
}) {
  const [data, setData] = useState<PreviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'email' | 'pdf'>('email')
  // Depend on the values, not the object - the caller builds a fresh object
  // each render, which would otherwise refetch in a loop.
  const draftSubject = draft?.subject
  const draftHtml = draft?.body_html
  const draftText = draft?.body_text

  useEffect(() => {
    let cancelled = false
    api
      .post<PreviewResponse>(`/admin/email-templates/${template.id}/preview/`, {
        context: {},
        ...(draftSubject !== undefined
          ? {
              subject: draftSubject,
              body_html: draftHtml,
              body_text: draftText,
            }
          : {}),
      })
      .then((res) => {
        if (cancelled) return
        setData(res.data)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setError('Could not render preview.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [template.id, draftSubject, draftHtml, draftText])

  const hasPdf = !!data?.pdf_base64
  const pdfDataUrl = hasPdf ? `data:application/pdf;base64,${data!.pdf_base64}` : ''

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-[960px] w-[92vw] max-h-[88vh] p-0 overflow-hidden flex flex-col sm:!max-w-[960px]">
        <DialogHeader className="px-6 py-4 border-b-[0.5px] border-b-[var(--rule-strong)]">
          <DialogTitle className="font-display font-light text-[22px] m-0">
            {template.name}
          </DialogTitle>
          <DialogDescription className="font-mono text-[11.5px] text-[var(--ink-3)] mt-1">
            {template.slug} · {template.kind}
          </DialogDescription>
        </DialogHeader>


        {hasPdf && (
          <div className="flex gap-1 px-6 pt-4 border-b-[0.5px] border-b-[var(--rule-light)]">
            <TabBtn active={tab === 'email'} onClick={() => setTab('email')}>
              Email body
            </TabBtn>
            <TabBtn active={tab === 'pdf'} onClick={() => setTab('pdf')}>
              PDF attachment
            </TabBtn>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-[var(--bg)]">
          {loading && (
            <div className="p-10 text-center text-[13px] text-[var(--ink-3)]">
              Rendering preview…
            </div>
          )}
          {error && (
            <div className="p-10 text-center text-[13px] text-[var(--ink-3)]">
              {error}
            </div>
          )}
          {data && tab === 'email' && (
            <div className="p-6">
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1">
                Subject
              </div>
              <div className="text-[14px] text-[var(--ink)] mb-4">{data.subject}</div>
              <iframe
                title="Email body preview"
                srcDoc={neutralisePreviewLinks(data.body_html || '')}
                sandbox="allow-same-origin"
                className="w-full h-[68vh] border-[0.5px] border-[var(--rule-strong)] rounded-lg bg-[var(--bg-card)]"
              />
              {data.static_attachments && data.static_attachments.length > 0 && (
                <div className="mt-4 rounded-lg border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] p-3">
                  <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">
                    PDF attachments
                  </div>
                  <ul className="space-y-1">
                    {data.static_attachments.map((att, i) => (
                      <li
                        key={`${att.name}-${i}`}
                        className="flex items-center gap-2 text-[13px]"
                      >
                        <Paperclip
                          size={13}
                          className="shrink-0 text-[var(--ink-3)]"
                          aria-hidden
                        />
                        {att.url ? (
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--ink)] underline decoration-[var(--rule-strong)] underline-offset-2 hover:decoration-[var(--ink)]"
                          >
                            {att.name}
                          </a>
                        ) : (
                          <span className="text-[var(--ink)]">{att.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {data && tab === 'pdf' && hasPdf && (
            <iframe
              title="PDF preview"
              src={pdfDataUrl}
              className="w-full h-[72vh] border-0 bg-white"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-2 text-[11px] tracking-[0.16em] uppercase border-b-[0.5px] -mb-[0.5px] transition-colors ' +
        (active
          ? 'border-b-[var(--ink)] text-[var(--ink)]'
          : 'border-b-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]')
      }
    >
      {children}
    </button>
  )
}

/**
 * Replace dummy `https://example.com/…` hrefs with `#preview`. Combined
 * with the iframe's `sandbox` attribute (no allow-top-navigation), clicks
 * on CTA buttons inside the preview can't navigate the admin app away.
 */
function neutralisePreviewLinks(html: string): string {
  return html.replace(
    /href="https?:\/\/example\.com\/[^"]*"/gi,
    'href="#preview"',
  )
}
